import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

const MENU_GAP = 8;
const MENU_MAX_HEIGHT = 260;
const VIEWPORT_PADDING = 12;

const AppCustomSelect = ({
  id,
  value,
  placeholder,
  options = [],
  onChange,
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const stringValue = value == null || value === "" ? "" : String(value);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [options]
  );

  const selectedOption = normalizedOptions.find((option) => option.value === stringValue);
  const displayText = selectedOption?.label || placeholder || "";
  const hasValue = Boolean(selectedOption) && stringValue !== "0" && stringValue !== "";

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const availableHeight = openUpward ? spaceAbove - MENU_GAP : spaceBelow - MENU_GAP;

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 10050,
      maxHeight: Math.max(120, Math.min(MENU_MAX_HEIGHT, availableHeight)),
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + MENU_GAP, top: "auto" }
        : { top: rect.bottom + MENU_GAP, bottom: "auto" }),
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();

    const handleOutsideClick = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen]);

  const handleSelect = (nextValue) => {
    if (disabled) {
      return;
    }

    setIsOpen(false);
    onChange(nextValue);
  };

  const menu =
    isOpen && menuStyle
      ? ReactDOM.createPortal(
          <ul
            ref={menuRef}
            className="t-eminPayCustomSelect-menu t-eminPayCustomSelect-menu--portal"
            style={menuStyle}
            role="listbox"
            aria-labelledby={id}
          >
            {normalizedOptions.map((option) => (
              <li key={option.value} className="t-eminPayCustomSelect-item">
                <button
                  type="button"
                  role="option"
                  aria-selected={stringValue === option.value}
                  className={`t-eminPayCustomSelect-option${
                    stringValue === option.value ? " is-selected" : ""
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`t-eminPayCustomSelect${isOpen ? " is-open" : ""}${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`}
    >
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`t-eminPayCustomSelect-trigger${hasValue ? "" : " is-placeholder"}`}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-selected-label={displayText}
        data-value={stringValue}
        disabled={disabled}
      >
        <span className="t-eminPayCustomSelect-value">{displayText}</span>
        <i className="t-icon-keyboard_arrow_down t-eminPayCustomSelect-chevron" aria-hidden="true" />
      </button>
      {menu}
    </div>
  );
};

export default AppCustomSelect;
